import { deviceHeight } from "@/app/activity/components/timeline/activityTimeline/constants"
import { useGetWidthPerTime } from "@/app/activity/components/timeline/activityTimeline/hooks"
import { getXPositionByDate } from "@/app/activity/components/timeline/activityTimeline/utils"
import { speedUnitLimits } from "@/app/activity/components/deviceTimeline/constants"
import { DeviceTimelineProps } from "@/app/activity/components/deviceTimeline/props"
import { DrawChartParams } from "@/app/activity/components/deviceTimeline/type"
import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"
import { useSpeedDataQuery } from "@/hooks/api/useDataQuery"
import { createGlobalStore } from "@/stores/global"
import { colors as designColors } from "@/components/colors"
import { colors } from "@mui/material"
import classNames from "classnames"
import { useEffect, useMemo, useRef, useState } from "react"

import "./deviceTimeline.scss"
import GuidLine from "@/app/activity/components/guideline/activityGuideLine/ActivityGuideline"
import SessionGuideLine from "@/app/activity/components/guideline/sessionGuideline/SessionGuideline"
import DataBox from "@/app/activity/detail/components/dataBox/DataBox"

const speedChartPadding = 5

export default function DeviceTimeline({
  activityData,
  className,
  sessionDataList,
  hideSessionGuideline = false,
  ...props
}: DeviceTimelineProps) {
  const ref = useRef<SVGSVGElement>(null)
  const [points, setPoints] = useState<string>("")
  const [isIntersect, setIsIntersect] = useState<boolean>(false)
  const { data: speedDataResponse } = useSpeedDataQuery(activityData.idfData, isIntersect)

  const { teamUnit } = createGlobalStore((state) => ({
    teamUnit: state.teamUnit,
  }))

  const { range } = createActivityCommonStore((state) => ({
    range: state.range,
  }))
  const { session, sessionGuideLine } = createSessionStore((state) => ({
    session: state.session,
    sessionGuideLine: state.sessionGuideLine,
  }))

  const {
    timelineWidth,
    remainRightArea = 0,
    sessionMouseGuideLine,
  } = createActivityCommonStore((state) => ({
    timelineWidth: state.timelineWidth,
    remainRightArea: state.remainingArea,
    sessionMouseGuideLine: state.sessionMouseGuideLine,
  }))

  const chartXSpace = useGetWidthPerTime({
    dateStart: range.dateStart || new Date(),
    dateFinish: range.dateFinish || new Date(),
    chartWidth: timelineWidth,
  })

  const teamSpeedUnit = useMemo(() => {
    return teamUnit.find((item) => item.type === "speed")?.unit || "km/h"
  }, [teamUnit])

  const dataX = useMemo(() => {
    const { dateStart: start, dateFinish: finish } = range
    if (speedDataResponse) {
      const { dateStart, dateFinish } = speedDataResponse

      return {
        start:
          getXPositionByDate(new Date(dateStart), start as Date, finish as Date) * timelineWidth,

        end:
          getXPositionByDate(new Date(dateFinish), start as Date, finish as Date) * timelineWidth,
      }
    }
  }, [range, speedDataResponse, timelineWidth, session])

  const drawSpeedDataByWorker = ({ height, magnification = 1 }: DrawChartParams) => {
    const { limit } = speedUnitLimits[teamSpeedUnit]

    const widthPerTime = chartXSpace * 1000

    if (!speedDataResponse || speedDataResponse.speedData.length === 0) {
      return
    }

    const points = speedDataResponse.speedData.reduce(
      (points, speed, i) =>
        `${points}${i * widthPerTime * magnification},${height - height * (speed / limit)} `,
      "",
    )

    setPoints(points)
  }

  useEffect(() => {
    drawSpeedDataByWorker({ height: deviceHeight - speedChartPadding * 2 })
  }, [speedDataResponse])

  useEffect(() => {
    if (ref.current) {
      const observer = new IntersectionObserver((entries) => {
        setIsIntersect(entries[0].isIntersecting)
      })
      observer.observe(ref.current as Element)

      return () => {
        observer.disconnect()
      }
    }
  }, [ref.current])

  useEffect(() => {
    drawSpeedDataByWorker({ height: deviceHeight - speedChartPadding * 2 })
  }, [chartXSpace])

  return (
    <>
      <svg
        className={classNames("DeviceTimelineCont", className)}
        width={timelineWidth + remainRightArea}
        height="100px"
        ref={ref}
        {...props}>
        {points ? (
          <g
            transform={`translate(${(dataX?.start || 0) + remainRightArea / 2} , 20)`}
            height={deviceHeight - speedChartPadding * 2}>
            <polyline
              points={points}
              style={{ fill: "none", stroke: designColors.neutral.platinum[900], strokeWidth: 1 }}
            />
          </g>
        ) : (
          <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle">
            loading...
          </text>
        )}
        <line x1="0" x2="100%" y1="100%" y2="100%" stroke={colors.grey[300]} strokeWidth={1} />
        <GuidLine isVisible isTimeVisible={false} isSession isHandleVisible />
        {!hideSessionGuideline && sessionGuideLine && (
          <SessionGuideLine
            isDateVisible={false}
            position={sessionGuideLine.startX}
            date={sessionGuideLine.startDate as Date}
          />
        )}

        {!hideSessionGuideline && sessionGuideLine?.endX && (
          <SessionGuideLine
            isDateVisible={false}
            position={sessionGuideLine.endX}
            date={sessionGuideLine.endDate as Date}
          />
        )}

        {sessionMouseGuideLine?.isVisible && !sessionGuideLine?.endX && (
          <SessionGuideLine
            isDateVisible={false}
            position={sessionMouseGuideLine.x}
            date={sessionMouseGuideLine.date as Date}
          />
        )}
        {sessionDataList?.map((sessionData, index) => (
          <DataBox
            key={`${sessionData.idfData}_${index}`}
            {...sessionData}
            originalDateStart={activityData.dateStart}
            originalDateFinish={activityData.dateFinish}
          />
        ))}
      </svg>
    </>
  )
}
